import React, { useState, useEffect } from 'react';
import api from '../api/client';

const PopupConfirmSeeds = ({ isOpen, onClose, onConfirm, selectedGTIN, onTotalWeightChange, initialPopupData, initialQty, plannedSubstitute }) => {
    const [qrCode, setQRCode] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [seedsID, setSeedsID] = useState(null);
    // Пакетик удобрения: мешки сырья ищутся по rawMaterialID, а не по seedsID
    const [productType, setProductType] = useState('seeds');
    const [rawMaterialID, setRawMaterialID] = useState(null);
    const isFert = productType === 'fert';
    const materialWord = isFert ? 'Fertilizer' : 'Seeds';
    const [qty, setQty] = useState(initialQty || '');
    const [gPerPacket, setGPerPacket] = useState(0);
    const [suggestedBags, setSuggestedBags] = useState([]);
    const [remainingWeight, setRemainingWeight] = useState(0);
    const [approxSeedsNeeded, setApproxSeedsNeeded] = useState(0);
    const [packName, setPackName] = useState('');
    const [seedsName, setSeedsName] = useState('');
    const [totalWeight, setTotalWeight] = useState(0);
    const minBags = 1;
    const maxBags = 8;
    const [bags, setBags] = useState([
        { seedsID: '', bagnumID: '', weight: 0, lot: 'Unknown', errorMessage: '' }
    ]);

    useEffect(() => {
        if (isOpen) {
            if (initialPopupData) {
                setBags(initialPopupData.bags && initialPopupData.bags.length > 0
                    ? initialPopupData.bags
                    : [{ seedsID: '', bagnumID: '', weight: 0, lot: 'Unknown', errorMessage: '' }]);
                setQty(initialPopupData.qtyToProduce || initialQty || '');
                setGPerPacket(initialPopupData.gPerPacket || 0);
                setApproxSeedsNeeded(initialPopupData.approxSeedsNeeded || 0);
                setSuggestedBags(initialPopupData.suggestedBags || []);
                setPackName(initialPopupData.packName || '');
                setSeedsName(initialPopupData.seedsName || '');
            } else {
                setBags([{ seedsID: '', bagnumID: '', weight: 0, lot: 'Unknown', errorMessage: '' }]);
                setQty(initialQty || '');
                setGPerPacket(0);
                setApproxSeedsNeeded(0);
                setSuggestedBags([]);
                setPackName('');
                setSeedsName('');
            }
        }
    }, [isOpen, initialPopupData, initialQty]);

    const handleAddBag = () => {
        if (bags.length < maxBags) {
            setBags([...bags, { seedsID: '', bagnumID: '', weight: 0, lot: 'Unknown', errorMessage: '' }]);
        }
    };

    const handleRemoveBag = (index) => {
        if (bags.length > minBags) {
            const updatedBags = [...bags];
            updatedBags.splice(index, 1);
            setBags(updatedBags);
            calculateTotalWeight(updatedBags);
        }
    };

    useEffect(() => {
        if (selectedGTIN) {
            api.get(`/api/packets/gtin/${selectedGTIN}`)
                .then(response => {
                    const d = response.data;
                    if (d && (d.seedsID || d.product_type === 'fert')) {
                        setSeedsID(d.seedsID || null);
                        setProductType(d.product_type || 'seeds');
                        setRawMaterialID(d.rawMaterialID || null);
                        setGPerPacket(response.data.gperpacket || 0);
                        setPackName(response.data.packName || 'Unknown');
                        setSeedsName(response.data.seedsName || 'Unknown');
                        setErrorMessage('');
                        setSuggestedBags([]);
                        setRemainingWeight(0);
                    } else {
                        setErrorMessage('Failed to retrieve seed information for the selected GTIN.');
                    }
                })
                .catch(error => {
                    console.error('Error fetching seed information:', error);
                    setErrorMessage('An error occurred while retrieving seed information.');
                });
        }
    }, [selectedGTIN]);

    const handleClosePopup = () => {
        setBags([]);
        setQty('');
        setGPerPacket(0);
        setPackName('');
        setSeedsName('');
        setApproxSeedsNeeded(0);
        setSuggestedBags([]);
        setRemainingWeight(0);
        setErrorMessage('');
        onClose();
    };

    useEffect(() => {
        if (gPerPacket > 0 && qty > 0) {
            const seedsNeeded = gPerPacket * qty * 0.00220462;
            setApproxSeedsNeeded(seedsNeeded.toFixed(2));
        } else {
            setApproxSeedsNeeded(0);
        }
    }, [gPerPacket, qty]);

    const handleQRCodeChange = (e) => {
        setQRCode(e.target.value);
    };

    const handleQtyChange = (e) => {
        setQty(e.target.value);
    };

    const handleInputChange = (e) => {
        setQRCode(e.target.value);
    };

    const handleCheckSeeds = async () => {
        let allBagsValid = true;

        const filteredBags = bags.filter(bag => bag.bagnumID);

        for (const bag of filteredBags) {
            // Подходит мешок из семян самого SKU или из отмеченной замены
            const { exists: isBagValid } = await verifyBagAndSeed(seedsID, bag.bagnumID);
            if (!isBagValid) {
                allBagsValid = false;
                break;
            }
        }

        if (allBagsValid) {
            const bagData = filteredBags.map(bag => ({
                bagnumID: bag.bagnumID,
                weight: bag.weight,
                lot: bag.lot
            }));
            onConfirm(true, bagData, qty, { productType, rawMaterialID, seedsID });
        } else {
            setErrorMessage('Some bags are incorrect. Please fix the errors before proceeding.');
        }
    };

    useEffect(() => {
        if (approxSeedsNeeded > 0 && (seedsID || rawMaterialID)) {
            fetchSuggestedBags(seedsID, approxSeedsNeeded);
        }
    }, [approxSeedsNeeded, seedsID, rawMaterialID]);

    // Для удобрения сервер подбирает мешки по rawMaterialID (только fert_bag_stock)
    const materialParams = () => (isFert ? { rawMaterialID } : {});

    const fetchSuggestedBags = async (seedsID, approxSeedsNeeded) => {
        try {
            const response = await api.get('/api/suggestedbags', {
                params: { seedsID, approxSeedsNeeded, ...materialParams() }
            });

            const suggestedBags = response.data;
            let totalWeight = 0;
            suggestedBags.forEach(bag => {
                totalWeight += bag.currentWeight;
            });

            const remainingWeight = approxSeedsNeeded - totalWeight;
            setSuggestedBags(suggestedBags);
            setRemainingWeight(remainingWeight > 0 ? remainingWeight : 0);
        } catch (error) {
            console.error('Error fetching suggested bags:', error);
        }
    };

    // { exists, isSubstitute, seedsName }: мешок есть у этих семян или у их замены
    const verifyBagAndSeed = async (seedsID, bagnumID) => {
        try {
            const response = await api.get('/api/verify-bag', {
                params: { seedsID, bagnumID, ...materialParams() }
            });
            return response.data;
        } catch (error) {
            console.error('Error verifying bag and seed:', error);
            return { exists: false };
        }
    };

    const handleBagNumberChange = async (index, event) => {
        const newBagNumber = event.target.value;
        const newBags = [...bags];
        newBags[index].bagnumID = newBagNumber;

        const minRequiredLength = 5;

        newBags[index].substituteName = '';

        if (isFert && newBagNumber.length >= minRequiredLength) {
            // Мешок удобрения: только из fert_bag_stock с сырьём этого продукта, семенной с тем же номером не пройдёт
            const scanned = await verifyBagAndSeed(null, newBagNumber);
            if (scanned.exists) {
                newBags[index].weight = scanned.weight;
                newBags[index].lot = scanned.lot || 'Unknown';
                newBags[index].errorMessage = '';
            } else {
                newBags[index].errorMessage = `Bag ${newBagNumber} is not a ${seedsName || 'fertilizer'} bag. Please check the bag or notify the manager.`;
            }
        } else if (newBags[index].seedsID && newBagNumber.length >= minRequiredLength) {
            // Номер мешка должен принадлежать именно отсканированным семенам
            const scanned = await verifyBagAndSeed(newBags[index].seedsID, newBagNumber);
            const isBagValid = scanned.exists && !scanned.isSubstitute;

            if (isBagValid) {
                const bagData = await fetchBagWeight(newBagNumber);
                newBags[index].weight = bagData.weight;
                newBags[index].lot = bagData.lot || 'Unknown';
                newBags[index].errorMessage = '';
                // Мешок не из основных семян SKU, а из отмеченной замены: показываем значок
                const forSku = await verifyBagAndSeed(seedsID, newBagNumber);
                if (forSku.isSubstitute) newBags[index].substituteName = forSku.seedsName || 'another seed';
            } else {
                newBags[index].errorMessage = 'The seed is correct, but this bag number is not in the inventory. Please notify the manager.';
            }
        } else {
            newBags[index].errorMessage = '';
        }

        setBags(newBags);
    };

    const fetchBagWeight = async (bagnumID) => {
        try {
            const response = await api.get('/api/bag-weight', {
                params: { bagnumID, ...materialParams() }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching bag weight:', error);
            return { weight: 0 };
        }
    };

    const handleSeedsIDChange = (index, event) => {
        const newSeedsID = event.target.value;
        const newBags = [...bags];
        newBags[index].seedsID = newSeedsID;
        setBags(newBags);
    };

    const calculateTotalWeight = () => {
        const total = bags.reduce((sum, bag) => sum + parseFloat(bag.weight || 0), 0);
        setTotalWeight(total);
        if (typeof onTotalWeightChange === 'function') {
            onTotalWeightChange(total);
        }
    };

    const handleWeightChange = (index, event) => {
        const newWeight = event.target.value;
        const newBags = [...bags];
        newBags[index].weight = newWeight;
        setBags(newBags);
        calculateTotalWeight();
    };

    return (
        <div className={`popup ${isOpen ? 'open' : ''}`}>
            <div className="popup__body">
                <div className="popup__content">
                    <button className="popup__close-button" onClick={handleClosePopup}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <use xlinkHref="#closeBtn"></use>
                        </svg>
                    </button>
                    <h2 className="popup__title">Check {materialWord}</h2>
                    <h3 className="popup__subtitle">Please Scan QR-code on {isFert ? 'fertilizer' : 'seeds'} bag to check</h3>
                    <div className="popup__inputs--columns">
                        <label className="popup__label">QTY to Produce</label>
                        <input
                            type="number"
                            className="popup__input"
                            value={qty}
                            onChange={handleQtyChange}
                            placeholder="Enter quantity to produce"
                        />
                        <label className="dynamic-label" style={{ margin: '7px 0 0 0' }}>Pack Name: {packName}</label>
                        <label className="dynamic-label" style={{ margin: '7px 0 0 0' }}>G per Packet: {gPerPacket}</label>
                        {isFert && (
                            <label className="dynamic-label" style={{ margin: '7px 0 0 0' }}>Fertilizer: {seedsName}</label>
                        )}
                        <label className="dynamic-label" style={{ margin: '5px 0 0 0' }}>Approx {materialWord} Needed: {approxSeedsNeeded}</label>
                        {plannedSubstitute && (
                            <label className="dynamic-label" style={{ margin: '5px 0 0 0', color: '#b45309' }}>
                                <i className="fa-solid fa-right-left" style={{ marginRight: 6 }}></i>
                                Planned with substitute seeds: {plannedSubstitute}
                            </label>
                        )}
                        <div className='suggested_bags'>
                            <label className='suggested_bags_label'>Suggested {isFert ? 'Fertilizer' : 'Seed'} Bags</label>
                            {suggestedBags.map((bag, index) => (
                                <div key={index} className='sug_bag_block'>
                                    {!!bag.isSubstitute && (
                                        <label className='sug_bag' style={{ color: '#d97706' }}
                                            title={`Substitute seed: ${bag.seedsName} can be used instead of ${seedsName}`}>
                                            <i className="fa-solid fa-right-left" style={{ marginRight: 6 }}></i>
                                            {bag.seedsName}
                                        </label>
                                    )}
                                    <label className='sug_bag'>Bag - {bag.bagnumID}</label>
                                    <label className='sug_bag'>Lot - {bag.lot}</label>
                                    <label className='sug_bag'>Weight - {bag.currentWeight}</label>
                                </div>
                            ))}
                            {remainingWeight > 0 && (
                                <div className='sug_bag_block'>
                                    <label className='sug_bag'>Still needed - {remainingWeight.toFixed(2)} pounds for production</label>
                                </div>
                            )}
                        </div>

                        <div>
                            <h3 className="tookbags__subtitle">Took Bags With {materialWord}</h3>
                            {[...bags].map((bag, index) => (
                                <div key={index} className="border-top-left-text">
                                    <label className='top-left-text'>Bag #{index + 1}</label>
                                    <div className="took_bags">
                                        {!isFert && (
                                            <>
                                                <label className="popup__label" style={{ margin: '12px 0 0 0' }}>Scan QR code with seed name</label>
                                                <input
                                                    type="text"
                                                    className="popup__input"
                                                    value={bags[index]?.seedsID || ''}
                                                    onChange={(e) => handleSeedsIDChange(index, e)}
                                                    placeholder="Seeds ID"
                                                />
                                            </>
                                        )}
                                        <label className="popup__label" style={{ margin: '10px 0 0 0' }}>Scan QR code with bag number</label>
                                        <input
                                            type="text"
                                            className="popup__input"
                                            value={bags[index]?.bagnumID || ''}
                                            onChange={(e) => handleBagNumberChange(index, e)}
                                            placeholder="Bag Number"
                                        />
                                        <label className="popup__label" style={{ margin: '12px 0 0 0' }}>Weight</label>
                                        <input
                                            type="text"
                                            className="popup__input"
                                            value={bags[index]?.weight || ''}
                                            onChange={(e) => handleWeightChange(index, e)}
                                            placeholder="Weight"
                                        />
                                        {bag.substituteName && (
                                            <div style={{ color: '#d97706', marginTop: 8, fontSize: 13 }}>
                                                <i className="fa-solid fa-right-left" style={{ marginRight: 6 }}></i>
                                                Substitute seed: {bag.substituteName} instead of {seedsName}
                                            </div>
                                        )}
                                        {bag.errorMessage && (
                                            <div style={{ color: 'red' }}>{bag.errorMessage}</div>
                                        )}
                                    </div>
                                    {bags.length > minBags && (
                                        <button
                                            className="remove-bag-button"
                                            onClick={() => handleRemoveBag(index)}
                                            style={{ color: 'gray', marginLeft: '10px' }}
                                        >
                                            Remove Bag
                                        </button>
                                    )}
                                </div>
                            ))}
                            {bags.length < maxBags && (
                                <a className='addmore' onClick={handleAddBag}>
                                    <i className="fas fa-plus-square addmore-icon" style={{ color: 'gray', marginRight: '8px' }}></i>
                                    Add More Bags
                                </a>
                            )}
                        </div>
                        <button className="popup__action-button" onClick={handleCheckSeeds}>
                            Check {materialWord} and Save
                        </button>
                        {errorMessage && <div className="popup__error-message">{errorMessage}</div>}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PopupConfirmSeeds;